import React, { useState, useEffect, useContext } from 'react';
import api from '../api';
import { AuthContext } from '../context/AuthContext';
import { Plus, X, HelpCircle } from 'lucide-react';

export default function ScreeningQuestions({ jobId, onSubmit }) {
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchQuestions = async () => {
    try {
      const res = await api.get(`/api/screening-questions/job/${jobId}`);
      setQuestions(res.data);
      
      // Initialize responses
      const initialResponses = {};
      res.data.forEach(q => {
        initialResponses[q.id] = '';
      });
      setResponses(initialResponses);
    } catch (err) {
      console.error('Failed to fetch screening questions:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchQuestions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleResponseChange = (questionId, value) => {
    setResponses({
      ...responses,
      [questionId]: value
    });
  };


  if (loading) {
    return <div className="text-center text-gray-500">Loading screening questions...</div>;
  }

  if (questions.length === 0) {
    return null; // No screening questions for this job
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <div className="flex items-center mb-4">
        <HelpCircle className="text-blue-600 mr-2" size={24} />
        <h3 className="text-xl font-bold">Screening Questions</h3>
      </div>
      <p className="text-gray-600 mb-6">Please answer the following questions to complete your application:</p>

      <div className="space-y-6">
        {questions.map((question, index) => (
          <div key={question.id} className="border-b pb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {index + 1}. {question.question_text}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>

            {question.question_type === 'text' && (
              <textarea
                value={responses[question.id] || ''}
                onChange={(e) => handleResponseChange(question.id, e.target.value)}
                required={question.required}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder="Your answer..."
              />
            )}

            {question.question_type === 'yes_no' && (
              <div className="flex gap-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name={`question_${question.id}`}
                    value="yes"
                    checked={responses[question.id] === 'yes'}
                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                    required={question.required}
                    className="mr-2"
                  />
                  Yes
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name={`question_${question.id}`}
                    value="no"
                    checked={responses[question.id] === 'no'}
                    onChange={(e) => handleResponseChange(question.id, e.target.value)}
                    className="mr-2"
                  />
                  No
                </label>
              </div>
            )}

            {question.question_type === 'multiple_choice' && question.options && (
              <div className="space-y-2">
                {question.options.map((option, idx) => (
                  <label key={idx} className="flex items-center">
                    <input
                      type="radio"
                      name={`question_${question.id}`}
                      value={option.value || option}
                      checked={responses[question.id] === (option.value || option)}
                      onChange={(e) => handleResponseChange(question.id, e.target.value)}
                      required={question.required}
                      className="mr-2"
                    />
                    {option.label || option}
                  </label>
                ))}
              </div>
            )}

            {question.question_type === 'number' && (
              <input
                type="number"
                value={responses[question.id] || ''}
                onChange={(e) => handleResponseChange(question.id, e.target.value)}
                required={question.required}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Enter a number..."
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Component for employers to manage screening questions
export function ManageScreeningQuestions({ jobId }) {
  const { token } = useContext(AuthContext);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState({
    question_text: '',
    question_type: 'text',
    required: true,
    options: []
  });
  const [showAddForm, setShowAddForm] = useState(false);

  const fetchQuestions = async () => {
    try {
      const res = await api.get(`/api/screening-questions/job/${jobId}`);
      setQuestions(res.data);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchQuestions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const handleAddQuestion = async () => {
    try {
      await api.post(`/api/screening-questions/job/${jobId}`, {
        questions: [newQuestion]
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewQuestion({
        question_text: '',
        question_type: 'text',
        required: true,
        options: []
      });
      setShowAddForm(false);
      fetchQuestions();
    } catch (err) {
      console.error('Failed to add question:', err);
      alert('Failed to add question: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await api.delete(`/api/screening-questions/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchQuestions();
    } catch (err) {
      console.error('Failed to delete question:', err);
      alert('Failed to delete question');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold">Screening Questions</h3>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus size={16} />
          Add Question
        </button>
      </div>

      {showAddForm && (
        <div className="bg-gray-50 p-4 rounded-lg mb-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Question Text</label>
              <textarea
                value={newQuestion.question_text}
                onChange={(e) => setNewQuestion({...newQuestion, question_text: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
                rows="2"
                placeholder="Enter your question..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Question Type</label>
              <select
                value={newQuestion.question_type}
                onChange={(e) => setNewQuestion({...newQuestion, question_type: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg"
              >
                <option value="text">Text Answer</option>
                <option value="yes_no">Yes/No</option>
                <option value="multiple_choice">Multiple Choice</option>
                <option value="number">Number</option>
              </select>
            </div>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={newQuestion.required}
                onChange={(e) => setNewQuestion({...newQuestion, required: e.target.checked})}
                className="mr-2"
              />
              <span className="text-sm">Required</span>
            </label>

            <div className="flex gap-2">
              <button
                onClick={handleAddQuestion}
                disabled={!newQuestion.question_text}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                Save Question
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {questions.length === 0 ? (
          <p className="text-gray-500 text-center py-4">No screening questions yet. Add questions to filter candidates.</p>
        ) : (
          questions.map((q, idx) => (
            <div key={q.id} className="flex items-start justify-between border rounded-lg p-3">
              <div className="flex-1">
                <div className="font-medium">{idx + 1}. {q.question_text}</div>
                <div className="text-sm text-gray-500 mt-1">
                  Type: {q.question_type} | {q.required ? 'Required' : 'Optional'}
                </div>
              </div>
              <button
                onClick={() => handleDeleteQuestion(q.id)}
                className="text-red-600 hover:text-red-800"
              >
                <X size={18} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
